import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  emptyManualForm,
  type CreativePreview,
  type ManualCampaignForm,
  type PreviewData,
  type SelectedScreenMeta,
  type StepThreeData,
} from '@/features/campaigns/types/campaign';
import { useCampaignOverviewQuery } from '@/features/campaigns/api/campaignDetailApi';
import {
  useCampaignStep4,
  useRelaunchCampaign,
  useRelaunchReprice,
} from '@/features/campaigns/api/useCampaignMutations';
import { useAvailableCouponsQuery } from '@/features/campaigns/api/useCouponQueries';
import type { AppliedPreviewCoupon } from '@/features/campaigns/components/PreviewStep/PreviewStep.types';
import { fetchWalletBalance } from '@/features/dashboard/api/dashboardApi';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { useAppSelector } from '@/shared/store/hooks';
import { isTeamMemberRole } from '@/shared/config/roles';
import { MAX_CAMPAIGN_AMOUNT, MIN_CAMPAIGN_AMOUNT } from '@/features/campaigns/constants/campaignOptions';
import { formatMoney } from '@/shared/lib/format/money';
import { ApiError, getErrorMessage } from '@/shared/api/errors';
import {
  todayIsoDate,
  tomorrowIsoDate,
  validateCampaignDateRange,
  enumerateDates,
} from '@/features/campaigns/utils/campaignUtils';
import {
  getDatesMissingSlots,
  formatDateList,
  normalizeDateKey,
  getFullyBookedDates,
  findDayDataInSlots,
} from '@/features/campaigns/components/ScheduleStep/scheduleStepHelpers';
import { useInstantAdAccess } from '@/features/profile/hooks/useInstantAdAccess';

type RelaunchStep = 1 | 2;

/** Owns the 2-step relaunch flow: pick a new date range + slots, review pricing, then pay via step4 — no create-wizard state is touched. */
export function useCampaignRelaunchPage() {
  const { id: campaignId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isTeamMember = isTeamMemberRole(
    useAppSelector((s) => s.auth.user?.role),
  );
  const instantAdAccess = useInstantAdAccess();

  const [currentStep, setCurrentStep] = useState<RelaunchStep>(1);
  const [campaignData, setCampaignData] = useState<ManualCampaignForm>(() =>
    emptyManualForm(),
  );
  const [submitData, setSubmitData] = useState<StepThreeData>({
    bookingSlots: [],
    campaignId: campaignId || '',
  });
  const [previewData, setPreviewData] = useState<PreviewData>({});
  const [creativePreview, setCreativePreview] = useState<CreativePreview>({
    url: '',
    type: '',
  });
  const [selectedScreensMeta, setSelectedScreensMeta] = useState<
    Record<string, SelectedScreenMeta>
  >({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [dateErrors, setDateErrors] = useState<Record<string, string>>({});
  const [depositOpen, setDepositOpen] = useState(false);

  const overviewQuery = useCampaignOverviewQuery(campaignId);
  const overview = overviewQuery.data?.data;

  const walletQuery = useQuery({
    queryKey: queryKeys.wallet.balance(),
    queryFn: fetchWalletBalance,
    refetchOnWindowFocus: false,
  });
  const availableBalance = Number(walletQuery.data?.data?.availableBalance ?? 0);

  const billboardIds = useMemo(
    () =>
      (overview?.billboards || [])
        .map((b) => b.billboardId || '')
        .filter((bid): bid is string => Boolean(bid)),
    [overview?.billboards],
  );

  // Hydrate the campaign name/duration/screens once the overview loads so
  // ScheduleStep + PreviewStep can reuse the same ManualCampaignForm shape.
  useEffect(() => {
    if (!overview) return;
    setCampaignData((prev) => ({
      ...prev,
      campaignName: overview.campaignName || prev.campaignName,
      customDuration:
        overview.customDuration != null
          ? String(overview.customDuration)
          : prev.customDuration,
    }));
    setSelectedScreensMeta(() => {
      const meta: Record<string, SelectedScreenMeta> = {};
      (overview.billboards || []).forEach((b) => {
        const bid = b.screenId || '';
        if (!bid) return;
        meta[bid] = {
          id: bid,
          screenName: b.screenName || 'Screen',
          thumbnail: '',
          addressLine: [b.address?.city, b.address?.state]
            .filter(Boolean)
            .join(', '),
          city: b.address?.city || 'Unknown',
        };
      });
      return meta;
    });
    const media = overview.mediaIds?.[0];
    if (media?.mediaUrl) {
      setCreativePreview({ url: media.mediaUrl, type: media.mediaType || '' });
    }
  }, [overview]);

  const relaunch = useRelaunchCampaign();
  const reprice = useRelaunchReprice();
  const step4 = useCampaignStep4();

  const campaignCost = Number(previewData?.pricing?.amountWithTax || 0);
  const couponDiscount = Number(
    previewData?.coupon?.discountAmount ??
    previewData?.pricing?.discountAmount ??
    previewData?.summary?.discountAmount ??
    0,
  );
  const payable = Number(
    previewData?.pricing?.totalAmount ||
    Math.max(0, campaignCost - couponDiscount),
  );
  const { coupons, isLoading: isCouponsLoading } = useAvailableCouponsQuery(
    campaignCost + couponDiscount,
    currentStep === 2,
  );

  const belowMinOrder = campaignCost < MIN_CAMPAIGN_AMOUNT;
  const exceedsMaxOrder = campaignCost > MAX_CAMPAIGN_AMOUNT;
  const launchShortfall = Math.max(0, payable - availableBalance);
  const needsTopUp =
    currentStep === 2 &&
    !belowMinOrder &&
    !exceedsMaxOrder &&
    launchShortfall > 0;
  const depositAmount = Math.ceil(launchShortfall * 100) / 100;
  const isBusy = relaunch.isPending || step4.isPending;

  const buildRelaunchPayload = (couponCode?: string) => ({
    campaignId: campaignId || '',
    name: campaignData.campaignName || overview?.campaignName,
    startDate: campaignData.startDate,
    endDate: campaignData.endDate,
    bookingSlots: submitData.bookingSlots,
    qrInfo: {},
    ...(couponCode !== undefined ? { couponCode } : {}),
  });

  /** Re-prices via relaunch API when a coupon is applied/removed on the preview. */
  const repriceWithCoupon = (code: string) => {
    if (!campaignId || reprice.isPending || relaunch.isPending) return;
    if (!campaignData.startDate || !campaignData.endDate) return;
    if (!submitData.bookingSlots.length) return;

    const applyPreview = (nextPreview: PreviewData) => {
      setPreviewData(nextPreview);
      const responseCode = nextPreview.coupon?.couponCode?.trim() || '';
      setSubmitData((prev) => ({
        ...prev,
        couponCode: code ? responseCode || code : '',
      }));
    };

    reprice.mutate(buildRelaunchPayload(code), {
      onSuccess: (data) => {
        const nextPreview = data?.data;
        if (!nextPreview) {
          toast.error(data?.message || 'Could not update pricing with coupon');
          return;
        }
        applyPreview(nextPreview);
      },
      onError: (error) => {
        const details =
          error instanceof ApiError &&
            error.details &&
            typeof error.details === 'object'
            ? (error.details as { data?: PreviewData }).data
            : undefined;
        if (details && (details.pricing || details.coupon || details.summary)) {
          applyPreview(details);
          return;
        }
        toast.error(
          getErrorMessage(error, 'Could not update pricing with coupon'),
        );
      },
    });
  };

  const appliedCoupons: AppliedPreviewCoupon[] = [];
  const previewCode = previewData.coupon?.couponCode?.trim();
  const userCode = submitData.couponCode?.trim();
  if (previewCode) {
    appliedCoupons.push({
      code: previewCode,
      discount: Number(previewData.coupon?.discountAmount || couponDiscount),
      autoApplied: Boolean(previewData.coupon?.autoApplied),
    });
  } else if (userCode) {
    appliedCoupons.push({
      code: userCode,
      discount: couponDiscount,
      autoApplied: false,
    });
  }

  const coupon = {
    applied: appliedCoupons,
    coupons,
    isLoading: isCouponsLoading,
    isBusy: reprice.isPending,
    onApply: (code: string) => {
      const trimmed = code.trim();
      if (trimmed) repriceWithCoupon(trimmed);
    },
    onRemove: (code: string) => {
      if (code) repriceWithCoupon('');
    },
  };

  const clearDateError = (key: string) => {
    setDateErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const minStart = instantAdAccess ? todayIsoDate() : tomorrowIsoDate();
      const errors = validateCampaignDateRange(
        campaignData.startDate,
        campaignData.endDate,
        minStart,
      );
      setDateErrors(errors);
      if (Object.keys(errors).length > 0) return;

      if (!submitData.bookingSlots.length) {
        toast.error('Select at least one time slot');
        return;
      }

      if (!submitData.appliedToAll) {
        const allDates = enumerateDates(
          campaignData.startDate,
          campaignData.endDate,
          366,
        );
        const slotsData = submitData.slotsData || {};
        const bookedHours = new Set(submitData.bookingSlots.map((s) => s.hour));

        const fullyBooked = getFullyBookedDates(allDates, slotsData);
        const fullyBookedSet = new Set(fullyBooked.map(normalizeDateKey));

        // Also exclude dates where none of the user's chosen hours are available
        for (const d of allDates) {
          const normD = normalizeDateKey(d);
          if (!fullyBookedSet.has(normD) && bookedHours.size > 0) {
            const dayData = findDayDataInSlots(d, slotsData);
            if (dayData && Array.isArray(dayData.availableSlots)) {
              const hasBookedHourAvail = dayData.availableSlots.some(
                (h) => bookedHours.has(h.hour) && Number(h.availableRepetitions ?? 0) > 0,
              );
              if (!hasBookedHourAvail) {
                fullyBookedSet.add(normD);
              }
            }
          }
        }

        const missingDates = getDatesMissingSlots(
          submitData.bookingSlots,
          allDates,
          undefined,
          Array.from(fullyBookedSet),
        );

        if (missingDates.length > 0) {
          toast.error(
            `Please select time slots for all available dates (${missingDates.length} date${
              missingDates.length === 1 ? '' : 's'
            } remaining: ${formatDateList(missingDates)}) or turn on Sync slots & repetitions.`,
          );
          return;
        }
      }

      if (!campaignId) {
        toast.error('Missing campaign — please start again from Campaigns.');
        return;
      }

      relaunch.mutate(buildRelaunchPayload(submitData.couponCode || ''), {
        onSuccess: (data) => {
          setPreviewData(data?.data || {});
          setCurrentStep(2);
          const total = Number(data?.data?.pricing?.amountWithTax || 0);
          if (total > MAX_CAMPAIGN_AMOUNT) {
            toast.error(
              `Order total exceeds the maximum of ${formatMoney(MAX_CAMPAIGN_AMOUNT)}. Reduce dates or slots to continue.`,
            );
          }
        },
      });
      return;
    }

    if (belowMinOrder) {
      toast.error(
        `Minimum order is ${formatMoney(MIN_CAMPAIGN_AMOUNT)}. Go back and increase slots to spend more than ${formatMoney(MIN_CAMPAIGN_AMOUNT)}.`,
      );
      return;
    }

    if (exceedsMaxOrder) {
      toast.error(
        `Maximum campaign order is ${formatMoney(MAX_CAMPAIGN_AMOUNT)}. Reduce dates or slots to continue.`,
      );
      return;
    }

    if (launchShortfall > 0) {
      if (isTeamMember) {
        toast.error(
          'Insufficient agency wallet balance. Ask your agency admin to add funds.',
        );
        return;
      }
      setDepositOpen(true);
      return;
    }

    step4.mutate(
      { campaignId: previewData.campaignId || campaignId },
      {
        onSuccess: () => {
          setIsDone(true);
          setShowSuccess(true);
        },
      },
    );
  };

  const handleDepositSuccess = () => {
    setDepositOpen(false);
    toast.success('Payment successful. Launching campaign…');
    step4.mutate(
      { campaignId: previewData.campaignId || campaignId },
      {
        onSuccess: () => {
          setIsDone(true);
          setShowSuccess(true);
        },
      },
    );
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      return;
    }
    navigate('/campaigns');
  };

  const handleSessionExpiry = () => {
    if (isDone) return;
    toast.error('Session expired please restart the relaunch');
    navigate('/campaigns');
  };

  return {
    campaignId,
    overviewQuery,
    overview,
    currentStep,
    campaignData,
    setCampaignData,
    dateErrors,
    clearDateError,
    billboardIds,
    submitData,
    setSubmitData,
    previewData,
    creativePreview,
    selectedScreensMeta,
    availableBalance,
    isBusy,
    coupon,
    needsTopUp,
    belowMinOrder,
    exceedsMaxOrder,
    launchShortfall,
    depositOpen,
    depositAmount,
    closeDeposit: () => setDepositOpen(false),
    handleDepositSuccess,
    handleNext,
    handleBack,
    handleSessionExpiry,
    showSuccess,
    setShowSuccess,
    navigate,
  };
}
